import React, { useEffect, useRef, useState } from 'react'
import { Calendar, momentLocalizer, View } from 'react-big-calendar'
import moment from 'moment'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  ListIcon,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
  List,
  useDisclosure,
} from '@chakra-ui/react'
import { ViewLoader } from 'components/page-level-loader'
import { dateFormat, datePickerFormat } from 'utils/date-time-utils'
import { isValidAndNonEmpty } from 'utils'
import { useTrades } from 'api/vendor-details'
import { useAvailableVendors, useFilteredVendors } from 'api/pc-projects'
import Select from 'components/form/react-select'
import { BiCalendar, BiDetail } from 'react-icons/bi'
import CalendarO from './wo-calendar'
import { SelectOption } from 'types/transaction.type'
import { CircleIcon } from 'icons/project-icons'

const localizer = momentLocalizer(moment)

interface Props {
  workOrder: any
  closeModal: any
  workOrderFormReturn: any
}

const VendorSlotEvent = ({ event }, handleSlotClick) => {
  return (
    <Box width={'100%'} height={'100%'} textAlign={'start'} zIndex={10000} display={'flex'}>
      <Box width={'100%'} height={'100%'}>
        <Text color="#345EA6" textAlign={'start'} width={'100%'} fontSize="12px">
          {event.title}
        </Text>
        {event?.vendor?.woId && (
          <Text color="#345EA6" fontSize="12px">
            Work order: {event?.vendor?.woId}
          </Text>
        )}
      </Box>
      <List w={'20'}>
        {event?.vendor?.woId ? (
          <ListItem color={'red'} fontSize={'12px'} display={'flex'}>
            <ListIcon as={CircleIcon} color="red.500" /> Busy
          </ListItem>
        ) : (
          <ListItem color={'green'} fontSize={'12px'} display={'flex'}>
            <ListIcon as={CircleIcon} color="green.500" />
            Free
          </ListItem>
        )}
      </List>
    </Box>
  )
}

export const WOCalendar: React.FC<Props> = ({ workOrder, workOrderFormReturn, closeModal }) => {
  const [tradeOptions, setTradeOptions] = React.useState<any>([])
  const [vendorOptions, setVendorOptions] = React.useState<any>([])
  const [selectedVendorId, setSelectedVendorId] = React.useState<any>([])
  const [vendorSkillId, setVendorSkillId] = React.useState(null)
  const [vendorId, setVendorId] = React.useState(null)
  const [selectedDate, setSelectedDate] = useState<Date>(CalendarO.getInstance().getDefaultDate())
  const [selectedSlot, setSelectedSlot] = React.useState<any>()
  const [selectedView, setSelectedView] = useState<View | undefined>(CalendarO.getInstance().getDefaultView())
  const [events, setEvents] = React.useState<any[]>([])

  const [selectedSlotLabel, setSelectedSlotLabel] = useState<string>()
  const [selectedVendorLabel, setSelectedVendorLabel] = useState<string>('')
  const [workOrderTrade, setWorkOrderTrade] = useState<SelectOption>()

  const { data: trades } = useTrades()

  const { data: availableVendors } = useAvailableVendors(vendorSkillId, workOrder?.id, selectedVendorId)

  const { vendors } = useFilteredVendors(vendorSkillId, workOrder?.projectId)

  const vendorIds = vendors?.map(({ id }) => id)

  useEffect(() => {
    if (!workOrderFormReturn) return

    setWorkOrderTrade(workOrderFormReturn.getValues()?.vendorSkillId)
    setVendorSkillId(workOrderFormReturn.getValues()?.vendorSkillId?.value)

    setVendorId(workOrderFormReturn.getValues()?.vendorId || null)
    setSelectedVendorId(workOrderFormReturn.getValues()?.vendorId.value || null)
  }, [workOrderFormReturn])

  const vendorColors = availableVendors
    ?.filter((v, i) => !vendorIds?.includes(v.id, i + 1))
    ?.map(v => {
      // const backgroundColor = CalendarO.getInstance().getBusyColor().bgColor;
      const backgroundColor = '#DBEFFF'
      // const color = CalendarO.getInstance().getBusyColor().textColor;
      const color = '#345EA6'
      return {
        id: v.id,
        color,
        backgroundColor,
      }
    })

  let vendorNames = vendors
    ?.filter((v, i) => !vendorIds?.includes(v.id, i + 1))
    ?.map(v => ({ resourceId: v.id, resourceName: v.companyName }))

  if (selectedVendorId) {
    vendorNames = [
      {
        resourceId: selectedVendorId,
        resourceName: vendors?.find(v => v.id === selectedVendorId)?.companyName || '----',
      },
    ]
  }

  React.useEffect(() => {
    if (!availableVendors?.length) {
      if (selectedSlot) clickHandler(selectedSlot)
      return
    }

    const _e = availableVendors.map(vendor => {
      const startDate = new Date(vendor?.startDateTime)
      const endDate = new Date(vendor?.endDateTime)

      return {
        title: vendor?.name,
        start: startDate,
        end: endDate,
        vendor: vendor,
        backgroundColor: vendorColors?.find(v => v.id === vendor.id)?.backgroundColor,
        color: vendorColors?.find(v => v.id === vendor.id)?.color,
        id: vendor?.id,
        resourceId: vendor?.id,
      }
    })

    setEvents(_e)
    if (selectedSlot) clickHandler(selectedSlot)
  }, [availableVendors])

  React.useEffect(() => {
    const options = [] as any
    if (vendors && vendors?.length > 0) {
      vendors?.forEach(v => {
        options.push({ label: v.companyName as string, value: v.id as number })
      })
    }

    options.unshift({ label: 'All', value: null })
    setVendorOptions(options)
  }, [vendors, selectedVendorId])

  React.useEffect(() => {
    const option = [] as any
    if (trades && trades?.length > 0) {
      trades?.forEach(t => {
        option.push({ label: t.skill as string, value: t.id as number })
      })
    }
    setTradeOptions(option)
  }, [trades])

  //const selectedVendor = vendors?.find(v => v.id === (selectedVendorId as any))

  const [, setRange] = React.useState({})

  const handleRangeChange = range => {
    setRange(range)
  }

  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isOpenInvalidSlotNotice,
    onOpen: onOpenInvalidSlotNotice,
    onClose: onCloseInvalidSlotNotice,
  } = useDisclosure()

  const handleSlotClick = slot => {
    if (selectedView === 'day' || selectedView === 'week') {
      const startDate = new Date(slot.start)
      const endDate = new Date(slot.end)

      const startHours = moment(startDate).format('hh') // startDate.getHours() % 12 || 12
      const endHours = moment(endDate).format('hh') //endDate.getHours() % 12 || 12
      const vendorId = slot.resourceId
      let clockFormat = 'PM'

      if (Number(startHours) === 8) clockFormat = 'AM'

      const slotTime = `${startHours}:00-${endHours}:00 ${clockFormat}` //"8:00-10:00 AM";
      const dateLabel = dateFormat(startDate) + ' ' + slotTime

      setSelectedSlotLabel(dateLabel)

      const slots = workOrderFormReturn?.getValues()?.workOrderTimeSlots

      const slotFound = slots.find(s => s.label === dateLabel)
      const vendorFound = vendors?.find(v => v.id === vendorId)

      setSelectedVendorLabel(vendorFound?.companyName || '')

      if (slotFound && vendorFound) {
        const event = {
          timeSlot: dateLabel,
          vendor: {
            startDate: startDate,
            name: vendorFound?.companyName,
            id: vendorFound?.id,
          },
        }
        setSelectedEvent(event)
        onOpen()
      } else {
        onOpenInvalidSlotNotice()
      }
    }
  }

  const handleEventClick = event => {
    if (selectedView !== 'day') {
      setSelectedDate(new Date(event.start))
      setSelectedView('day')
    }
    if (event?.type === 'emptySlot') handleSlotClick(event)
  }

  const getEventStyle = React.useCallback(event => {
    const bgColor = event?.backgroundColor || '#3174ad'
    const color = event?.color || '#fff'
    return {
      style: {
        backgroundColor: bgColor,
        color,
        border: 'none',
      },
    }
  }, [])

  const [selectedEvent, setSelectedEvent] = useState<any>(null)

  const alertDialogCancelRef = useRef<any>(null)

  const handleConfirm = () => {
    const event = selectedEvent

    const slots = workOrderFormReturn?.getValues()?.workOrderTimeSlots

    if (slots?.length && event) {
      const timeSlot = event.timeSlot

      const slotFound = slots.find(s => s.label === timeSlot)

      if (slotFound) {
        setTimeout(() => {
          workOrderFormReturn?.setValue('workOrderStartTime', slotFound)
          workOrderFormReturn?.setValue('vendorId', {
            label: event.vendor.name,
            title: event.vendor.name,
            value: event.vendor.id,
          })
          workOrderFormReturn?.setValue(
            'vendorSkillId',
            tradeOptions?.find(t => t.value === vendorSkillId),
          )

          workOrderFormReturn?.setValue('workOrderStartDate', datePickerFormat(event.vendor.startDate))
          workOrderFormReturn?.setValue('workOrderExpectedCompletionDate', datePickerFormat(event.vendor.startDate))

          workOrderFormReturn?.submitRef.current.click()

          closeModal()
        }, 1000)

        onClose()
      }
    }
  }

  const handleConfirmClose = () => {
    setSelectedEvent(null)
    setSelectedSlotLabel('')
    setSelectedVendorLabel('')
    onClose()
  }

  const handleInvalidSlotNoticeClose = () => {
    setSelectedSlotLabel('')
    setSelectedVendorLabel('')
    onCloseInvalidSlotNotice()
  }

  const handleNavigate = (newDate, view) => {
    setSelectedDate(newDate)
  }

  const clickHandler = id => {
    setSelectedSlot(id)
    const date = workOrder?.clientSchedule?.[id].preferredStartDate
      ? workOrder?.clientSchedule?.[id].preferredStartDate
      : new Date()
    setSelectedDate(date)
    const timeArray = workOrder?.clientSchedule?.[id]?.preferredStartTime?.label?.split('-').map(date => {
      if (date.split(' ').length === 1) {
        const hours = Number(date.split(':')[0])
        if (hours === 8 || hours === 10) {
          return `${date.split(' ')[0]}:00 AM`
        } else {
          return `${date.split(' ')[0]}:00 PM`
        }
      } else return `${date.split(' ')[0]}:00 ${date.split(' ')[1]}`
    })

    const myEvent = [] as any

    const startDate = new Date(workOrder?.clientSchedule[id].preferredStartDate + ` ${timeArray[0]}`)
    const endDate = new Date(workOrder?.clientSchedule[id].preferredStartDate + ` ${timeArray[1]}`)

    if (selectedVendorId) {
      const vendor = vendors?.find(v => v.id === selectedVendorId)
      if (vendor) {
        myEvent.push({
          title: `Preferred Slot ${id + 1}`,
          type: 'emptySlot',
          start: startDate,
          end: endDate,
          vendor: vendor,
          backgroundColor: '#F2F3F4',
          color: '#345EA6',
          id: vendor.id,
          resourceId: vendor.id,
          onclick: handleEventClick,
        })
      }
    } else {
      vendorOptions.forEach((option, i) => {
        const vendor = vendors?.find(v => v.id === option.value)
        if (vendor)
          myEvent.push({
            title: `Preferred Slot ${id + 1}`,
            type: 'emptySlot',
            start: startDate,
            end: endDate,
            backgroundColor: '#F2F3F4',
            color: '#345EA6',
            id: vendor.id,
            resourceId: vendor.id,
            onclick: handleEventClick,
          })
      })
    }

    const removedSelectedSlotEvent = events.filter(event => event?.type !== 'emptySlot')

    const filteredMyEvent = myEvent.filter(
      event =>
        !removedSelectedSlotEvent.find(
          val => new Date(val.start).getTime() === new Date(event.start).getTime() && val?.id === event?.id,
        ),
    )
    setEvents([...removedSelectedSlotEvent, ...filteredMyEvent])
  }

  const invalidSlotDialogCancelRef = useRef<any>(null)

  return (
    <Stack gap="10px" minH="60vh" width="100%">
      {!workOrder && <ViewLoader />}

      {workOrder && (
        <>
          <Box padding="20px">
            <SimpleGrid columns={3} mt="20px" gap="30px" width="100%">
              {workOrder?.clientSchedule?.map((cs, idx) => (
                <Button
                  variant="ghost"
                  position="relative"
                  padding="10px"
                  border="1px solid #CBD5E0"
                  borderRadius="4px"
                  backgroundColor={selectedSlot === idx ? '#F7FAFC' : '#FFF'}
                  key={idx}
                  height="80px"
                  cursor={'pointer'}
                  zIndex="100"
                  onClick={() => {
                    clickHandler(idx)
                  }}
                >
                  <Text
                    mt="6px"
                    position="absolute"
                    background="#fff"
                    zIndex="5"
                    top="-17px"
                    height="12px"
                    width="120px"
                    left="22px"
                    marginLeft="10px"
                    paddingLeft="7px"
                    fontWeight="500"
                    color="gray.700"
                    fontSize="14px"
                  >
                    Preferred Slot: {idx + 1}
                  </Text>
                  <HStack gap="20px" mt="10px" justifyContent="center">
                    <InformationCard
                      title="Date"
                      date={dateFormat(cs.preferredStartDate)?.toString()}
                      icon={BiCalendar}
                    />
                    <InformationCard title="Time" date={cs.preferredStartTime?.label?.toString()} icon={BiDetail} />
                  </HStack>
                </Button>
              ))}
            </SimpleGrid>

            <Box mt="20px">
              <Divider borderColor="#CBD5E0" />
            </Box>
          </Box>
          <Box pos="relative" top="-20px">
            <HStack gap="20px" padding="20px" paddingBottom="0" paddingTop="0">
              <Box w="250px">
                <FormControl height="40px" data-testid="vendorSkillId" zIndex="99">
                  <FormLabel fontSize="14px" fontWeight={500} color="gray.600">
                    Trade
                  </FormLabel>
                  <Select
                    options={tradeOptions}
                    size="md"
                    onChange={option => {
                      setVendorSkillId(option.value)
                      setSelectedVendorId(null)
                      setVendorId(null)
                      setWorkOrderTrade(option)
                    }}
                    value={workOrderTrade}
                    selectProps={{ isBorderLeft: true, menuHeight: '150px' }}
                  />
                </FormControl>
              </Box>
              <Box w="250px" marginTop="calc(var(--chakra-space-7) * -1)">
                <FormControl height="40px" data-testid="vendorSkillId" zIndex="99">
                  <FormLabel fontSize="14px" fontWeight={500} color="gray.600">
                    Vendor
                  </FormLabel>
                  <Select
                    options={vendorOptions}
                    value={vendorId}
                    size="md"
                    onChange={option => {
                      setSelectedVendorId(option.value)
                      setVendorId(option)
                      // if (selectedSlot) clickHandler(selectedSlot);
                    }}
                    selectProps={{ isBorderLeft: true, menuHeight: '125px' }}
                  />
                </FormControl>
              </Box>
            </HStack>
          </Box>

          <Box mb="20px" padding="20px">
            <Calendar
              dayLayoutAlgorithm="no-overlap"
              localizer={localizer}
              events={events}
              resourceIdAccessor="resourceId"
              resources={vendorNames}
              resourceTitleAccessor="resourceName"
              startAccessor="start"
              endAccessor="end"
              style={{ height: 440, fontSize: '15px' }}
              date={selectedDate}
              view={selectedView}
              onRangeChange={handleRangeChange}
              onView={newView => setSelectedView(newView)}
              views={['week', 'day']}
              onSelectEvent={handleEventClick}
              eventPropGetter={getEventStyle}
              min={CalendarO.getInstance().getMinTime()}
              max={CalendarO.getInstance().getMaxTime()}
              onNavigate={handleNavigate}
              slotPropGetter={() => ({ style: { backgroundColor: '#fff' } })}
              selectable={true}
              timeslots={1}
              step={120}
              onSelectSlot={handleSlotClick}
              components={{
                event: e => VendorSlotEvent(e, handleSlotClick),
              }}
            />
          </Box>
        </>
      )}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={alertDialogCancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              borderBottom="1px solid #eee"
              fontSize="16px"
              color="#4A5568"
              fontWeight="500"
              fontFamily="Poppins"
              padding={2}
            >
              <Text ml={3}>Confirm</Text>
            </AlertDialogHeader>
            <AlertDialogBody fontFamily="Poppins" fontSize="14px" fontStyle="normal" fontWeight="400" color="#4A5568">
              Work order schedule will be updated with the selected slot ({selectedSlotLabel}) for company{' '}
              {selectedVendorLabel}.
            </AlertDialogBody>
            <AlertDialogFooter borderTop="1px solid #eee" marginTop={3} padding={3}>
              <Button
                ref={alertDialogCancelRef}
                onClick={handleConfirmClose}
                variant="outline"
                colorScheme="brand"
                size="sm"
              >
                Cancel
              </Button>
              <Button ml={3} onClick={handleConfirm} colorScheme="brand" variant="solid" size="sm">
                Ok
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={isOpenInvalidSlotNotice}
        leastDestructiveRef={invalidSlotDialogCancelRef}
        onClose={onCloseInvalidSlotNotice}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              borderBottom="1px solid #eee"
              fontSize="16px"
              color="#4A5568"
              fontWeight="500"
              fontFamily="Poppins"
              padding={2}
            >
              <Text ml={3}>Invalid Slot</Text>
            </AlertDialogHeader>
            <AlertDialogBody fontFamily="Poppins" fontSize="14px" fontStyle="normal" fontWeight="400" color="#4A5568">
              Selected slot ({selectedSlotLabel}) does not match with any client preferred slots.
            </AlertDialogBody>
            <AlertDialogFooter borderTop="1px solid #eee" marginTop={3} padding={3}>
              <Button
                ref={invalidSlotDialogCancelRef}
                onClick={handleInvalidSlotNoticeClose}
                ml={3}
                colorScheme="brand"
                variant="solid"
                size="sm"
              >
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  )
}

const InformationCard = props => {
  return (
    <Flex zIndex="10">
      <Box lineHeight="20px">
        <HStack mb="5px">
          <Icon as={props.icon} color="#4A5568" width="16px" height="16px" />

          <Text color="gray.700" fontWeight={500} fontSize="14px" fontStyle="normal" mb="1">
            {props.title}
          </Text>
        </HStack>
        <Text
          data-testid={props.testId}
          color="gray.600"
          fontSize="14px"
          fontStyle="normal"
          fontWeight={400}
          isTruncated={true}
          maxW="150px"
          title={props.date}
          {...props.customStyle}
        >
          {isValidAndNonEmpty(props.date) ? props.date : '----'}
        </Text>
      </Box>
    </Flex>
  )
}
